import "react-datepicker/dist/react-datepicker.css";

import { Field, useField } from "formik";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import { FieldErrorMessage, FieldHeader, FieldHeaderProps, FieldStateProps, TextField } from "../";
import { formatDateFieldValue } from "../../helpers/dateHelpers";
import { isHandheldOptimizationSelected } from "../../helpers/handheldOptimizationHelpers";
import {
  isXboxPlayAnywhereSelected,
  xboxPlayAnywhereRequiredPlatforms,
} from "../../helpers/xboxPlayAnywhereHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { TargetPlatforms, TargetPlatformsDisplayName } from "../../models/targetPlatforms";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import * as styles from "./TargetPlatformPicker.module.css";

type TargetPlatformPickerProps = Readonly<{
  name: string;
}> &
  FieldHeaderProps &
  FieldStateProps;

type TargetPlatformCheckBoxProps = Readonly<{
  text: string;
  name: string;
  optionName: string;
  optionNameValue: string;
  optionReleaseDateName: string;
  otherOptionName: string;
  isSelectionLocked?: boolean;
  disabled?: boolean;
}> &
  FieldStateProps;

const TargetPlatformCheckBox = (props: TargetPlatformCheckBoxProps) => {
  const {
    text,
    optionReleaseDateName,
    name,
    optionName,
    optionNameValue,
    otherOptionName,
    isSelectionLocked,
    isSaving,
  } = props;
  const [targetPlatformsReleaseDateTitle, otherPlatformNameTitle] = useStrings(
    "targetPlatformsReleaseDateTitle",
    "otherPlatformNameTitle"
  );
  const [field, _meta, helpers] = useField({ name: name });
  const [nameField, _nameMeta, nameHelpers] = useField({ name: optionName });
  const [releaseDateField, _releaseDateMeta, releaseDateHelpers] = useField({
    name: optionReleaseDateName,
  });
  const checkBoxEnabled = isSelectionLocked || Boolean(field.value);
  const [selectedDate, setSelectedDate] = useState(
    releaseDateField.value ? new Date(formatDateFieldValue(releaseDateField.value)) : undefined
  );
  const { setValue: setSelectedValue } = helpers;
  const { setValue: setOptionNameValue } = nameHelpers;
  const { setValue } = releaseDateHelpers;
  useEffect(() => {
    if (field.value === null || field.value === undefined) {
      setSelectedValue(false, false);
    }
    if (nameField.value !== optionNameValue) {
      setOptionNameValue(optionNameValue, false);
    }
  }, [field.value, nameField.value, optionNameValue, setOptionNameValue, setSelectedValue]);

  useEffect(() => {
    if (isSelectionLocked && field.value !== true) {
      setSelectedValue(true, false);
    }
  }, [field.value, isSelectionLocked, setSelectedValue]);

  useEffect(() => {
    if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      setValue(`${year}-${month}-${day}`);
    }
  }, [selectedDate, setValue]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = isSelectionLocked ? true : event.target.checked;
    setSelectedValue(selected);
    setOptionNameValue(optionNameValue);
  };

  return (
    <Row className={commonStyles.slimContainer}>
      <Column>
        <div className={styles.calendarContainer}>
          <label className={styles.option}>
            <Field
              type="checkbox"
              name={name}
              data-testid={name}
              checked={checkBoxEnabled}
              onChange={onChange}
              disabled={isSaving}
              aria-disabled={isSelectionLocked}
            />
            {text}
          </label>
          {checkBoxEnabled && (
            <>
              <Row>
                <Column>
                  <Row>
                    <div className={styles.flex}>
                      <DatePicker
                        data-testid={releaseDateField.name}
                        disabled={isSaving}
                        selected={selectedDate}
                        className={styles.calendar}
                        id={releaseDateField.name}
                        onChange={(date: Date) => setSelectedDate(date)}
                        placeholderText={targetPlatformsReleaseDateTitle}
                      />
                    </div>
                  </Row>
                  {optionNameValue === TargetPlatforms.other && (
                    <TextField
                      name={otherOptionName}
                      title={otherPlatformNameTitle}
                      isRequiredField={true}
                      isSaving={isSaving}
                    />
                  )}
                </Column>
              </Row>

              <FieldErrorMessage fieldName={releaseDateField.name} />
            </>
          )}
        </div>
      </Column>
    </Row>
  );
};

export const TargetPlatformPicker = (props: TargetPlatformPickerProps) => {
  const { name, isSaving } = props;
  const [xboxPlayAnywhereField] = useField<string | undefined>("xboxPlayAnywhere");
  const [handheldOptimizationField] = useField<string | undefined>("handheldOptimization");
  const requiresXboxPlayAnywherePlatforms = isXboxPlayAnywhereSelected(xboxPlayAnywhereField.value);
  const requiresHandheldOptimizationPlatform = isHandheldOptimizationSelected(
    handheldOptimizationField.value
  );
  const [microsoftStoreOnWindowsPlatform, other] = useStrings(
    "microsoftStoreOnWindowsPlatform",
    "other"
  );
  const getPlatformLabel = (platform: string) => {
    switch (platform) {
      case TargetPlatforms.windowsStore:
        return microsoftStoreOnWindowsPlatform;
      case TargetPlatforms.other:
        return other;
      default:
        return TargetPlatformsDisplayName[platform];
    }
  };

  return (
    <Row className={commonStyles.internalContainer}>
      <Column>
        <FieldHeader titleClassName={styles.optionTitle} {...props} />
        {Object.keys(TargetPlatforms).map((platform, index) => (
          <TargetPlatformCheckBox
            key={`${name}-${index}`}
            text={getPlatformLabel(platform)}
            name={`${name}.${index}.selected`}
            optionName={`${name}.${index}.platform`}
            optionNameValue={platform}
            optionReleaseDateName={`${name}.${index}.releaseDate`}
            otherOptionName={`${name}.${index}.otherPlatformName`}
            isSelectionLocked={
              (requiresXboxPlayAnywherePlatforms &&
                xboxPlayAnywhereRequiredPlatforms.includes(
                  platform as (typeof xboxPlayAnywhereRequiredPlatforms)[number]
                )) ||
              (requiresHandheldOptimizationPlatform && platform === TargetPlatforms.windowsStore)
            }
            isSaving={isSaving}
          />
        ))}
      </Column>
    </Row>
  );
};
